"use client"

import ChatInput from "@sourcepilot/ui/shell/chat/chatInput"
import { useState, useEffect } from "react"

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useSimpleAuth as useAuth } from "@sourcepilot/ui/shell/auth/use-simple-auth"
import { useAdminMode } from "@sourcepilot/ui/shell/auth/use-admin-mode"
import { productMiniAppStateAtom } from '@/state/rflp-session';import { patchStateAtom } from '@sourcepilot/ui/framework/state/session-data';import { useTabData } from '@sourcepilot/ui/framework/state/session-data';import { pendingChatMessagesAtom } from '@sourcepilot/ui/framework/state/session-data';import { useNeoClient } from '@sourcepilot/ui/framework/data/hooks/use-neo-client'
import { useAgents, AgentInfo } from '@sourcepilot/ui/framework/data/hooks/use-agents'
import { ModelRegistry } from '@sourcepilot/generated/registries/model-registry'

import { ChatTask } from '@sourcepilot/generated/pdm_tasks/api/chattask';

interface StarterProps {
  title: string
  description: string
  example: string
  onClick: (example: string) => void
}


function Starter({ title, description, example, onClick }: StarterProps) {
  return (
    <button
      onClick={() => onClick(example)}
      className="flex flex-col gap-0 px-4 rounded-[4px] border border-gray-200 hover:border-black transition-colors text-left group"
    >
      <h3 className="font-semibold text-gray-800 text-lg my-1">{title}</h3>
      <span className="text-sm text-justify text-muted-foreground leading-relaxed my-1">{description}</span>
      <span className="text-sm text-justify text-muted-foreground italic my-1 group-hover:text-black transition-all">"{example}"</span>
    </button>
  )
}

export default function WorkspaceContent() {
  const { user } = useAuth();
  const { isAdmin } = useAdminMode();
  const { navigateToTab, replaceActiveTab, activeTab } = useTabData();
  const { agents, defaultAgent } = useAgents();

  // Initialize API client for NeoNode operations (save, delete, etc.)
  useNeoClient();

  const [inputValue, setInputValue] = useState("");
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | undefined>(undefined);
  const [ patchState, _ ] = useAtom(patchStateAtom);
  const productMiniAppState = useAtomValue(productMiniAppStateAtom);
  const setPendingMessages = useSetAtom(pendingChatMessagesAtom);

  // Use selected agent (admin) or default agent
  const activeAgent = selectedAgent || defaultAgent;

  const handleStarterClick = (example: string) => {
    setInputValue(example)
  }

  /**
   * Create a new chat Task and navigate to it.
   *
   * Flow:
   * 1. Create Task via create API (backend auto-creates Thread via hooks)
   * 2. Store the initial message in pendingChatMessagesAtom
   * 3. Navigate to the Task immediately
   * 4. ChatView picks up pending message, waits for Thread via DAG, then sends modify
   */
  async function newProductStep(txt: string) {
    try {
      // 1. Create new Task via create API (uses selected agent's task class)
      const taskClassName = activeAgent?.task_class_name || 'ChatTask';
      const TaskClass = ModelRegistry[taskClassName] || ChatTask;
      // Use the first message (whitespace-collapsed) as the chat title.
      // Don't hard-truncate or append an ellipsis here — the UI truncates
      // titles to the available width with a real CSS ellipsis (mini-app
      // list, tabs), so a stored "..." just produces a redundant/short name.
      const task = new TaskClass({
        task_name: txt.trim().replace(/\s+/g, ' '),
      });
      const savedTask = await task.save();

      if (!savedTask.id) {
        throw new Error('Task created but no ID returned');
      }

      // 2. Store the initial message for ChatView to pick up and send
      setPendingMessages(prev => ({
        ...prev,
        [savedTask.id!]: {
          text: txt,
          timestamp: Date.now(),
        }
      }));

      // 3. Navigate to chat tab immediately
      const newChatTab = {
        id: savedTask.id,
        title: txt.length > 30 ? `${txt.slice(0, 30)}...` : txt,
        contentType: 'chat',
        taskClassName,
      };

      if (activeTab && activeTab.id === 'new' && activeTab.contentType === 'chat') {
        replaceActiveTab(newChatTab);
        console.log(`🔄 Replaced "New Chat" tab with task: ${savedTask.id}`);
      } else {
        navigateToTab(newChatTab);
        console.log(`🔄 Opened chat tab for task: ${savedTask.id}`);
      }

      return savedTask;
    } catch (error) {
      console.error('Failed to create chat task:', error);
      return null;
    }
  }

  // ws workspace patch update
  useEffect(() => {
    const patch_data = patchState.ids; // get a copy
    if (!patchState) {
      console.warn('null patch data');
      return;
    }

    if (patchState._type === 'Workspace') {
      console.log(`socket.[${patchState._type}].patch`);
      // TODO: do something here with the pacth
      sessionStorage.removeItem("patchState");  // delete from storage
    }
  }, [patchState]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-77px)] px-[5px]">
      {/* icon */}
      <div className="flex flex-col items-center mb-8">
        <img
          src="/sourcepilot-wordmark-dark.png"
          alt="Sourcepilot Logo"
          className="w-[340px] mb-4"
        />
        <h2 className="text-2xl font-semibold text-gray-800 border-b-0 mt-0 mb-2">What can we help you make?</h2>
      </div>

      {/* starters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl w-full px-4 mb-8">
        <Starter
          title="Prototype Products"
          description="Build your assembly tree through requirements and supply chain criteria."
          example="I want make a drone which can carry a 4kg payload for up to 5km. Parts should be sourced in Europe."
          onClick={handleStarterClick}
        />
        <Starter
          title="Find Components"
          description="Find components which match your requirements."
          example="I need a motor to power a washing machine. It should be as cheap as possible with high energy efficiency."
          onClick={handleStarterClick}
        />
        <Starter
          title="Optimize a BOM"
          description="Upload your existing BOM and we'll optimize it for you."
          example="Here is my BOM, do you have any suggestions how to make it more sustainable?"
          onClick={handleStarterClick}
        />
      </div>

      {/* chat input */}
      <div className="w-full px-[5px] max-w-[800px]">
        {/* Admin agent selector */}
        {isAdmin && agents.length > 1 && (
          <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
            <span>Agent:</span>
            <select
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
              value={activeAgent?.name || ''}
              onChange={(e) => {
                const agent = agents.find(a => a.name === e.target.value);
                setSelectedAgent(agent);
              }}
            >
              {agents.map(a => (
                <option key={a.name} value={a.name}>{a.label}</option>
              ))}
            </select>
          </div>
        )}
        <ChatInput
          chatSlug={'workspace'}
          onSend={newProductStep}
          value={inputValue}
        />
      </div>
    </div>
  )
}